import { YardTaskBlockReason } from '@nova/core';

import { formatTaskNumber } from './yardTaskFormatting';

import type { Maybe, YardTaskBlockDetail } from '@nova/core';

export type YardTaskBlockMessageSegment = {
  text: string;
  bold: boolean;
};

/** Matches the design spec's own markup: `**word**` marks the bit to render bold. */
const BOLD_MARKER = /\*\*(?<bold>.+?)\*\*/gu;

function resolveRawMessage(
  detail: Maybe<YardTaskBlockDetail>,
  t: (key: string, params?: Record<string, unknown>) => string
): string | null {
  if (!detail || detail.reason === null) {
    return null;
  }

  const base = 'core.yard.messages.blockReasons';

  switch (detail.reason) {
    case YardTaskBlockReason.RESERVED_BY_OTHER: {
      const taskNumber = formatTaskNumber(detail.byTaskRefNum);

      return taskNumber
        ? t(`${base}.reservedByOther`, { taskNumber })
        : t(`${base}.reservedByOtherGeneric`);
    }
    case YardTaskBlockReason.DESTINATION_CLOSED:
      return t(`${base}.destinationClosed`);
    case YardTaskBlockReason.DESTINATION_OCCUPIED:
      return t(`${base}.destinationOccupied`);
    case YardTaskBlockReason.ANOTHER_ASSET_IN_TRANSIT_FOR_DESTINATION:
      return t(`${base}.anotherAssetInTransitForDestination`, { assetCode: detail.assetCode });
    case YardTaskBlockReason.ASSET_IN_TRANSIT_ELSEWHERE:
      return t(`${base}.assetInTransitElsewhere`);
    default:
      return null;
  }
}

/**
 * One message per block reason (OD-4117), split on the `**bold**` markers the translated
 * string carries so the banner can render the highlighted words in bold via safe text
 * interpolation — never `v-html`, since one of them (an asset code) can be data a user typed.
 * `detail` comes straight off the backend's live-computed `getYardTaskBlockReason`.
 */
export function getYardTaskBlockMessageSegments(
  detail: Maybe<YardTaskBlockDetail>,
  t: (key: string, params?: Record<string, unknown>) => string
): YardTaskBlockMessageSegment[] | null {
  const raw = resolveRawMessage(detail, t);

  if (raw === null) {
    return null;
  }

  return raw
    .split(BOLD_MARKER)
    .map((text, index) => ({ text, bold: index % 2 === 1 }))
    .filter(segment => segment.text !== '');
}

/** Flat string form for contexts that can't render bold — toasts, logs. */
export function getYardTaskBlockMessage(
  detail: Maybe<YardTaskBlockDetail>,
  t: (key: string, params?: Record<string, unknown>) => string
): string | null {
  const raw = resolveRawMessage(detail, t);

  return raw === null ? null : raw.replace(/\*\*/gu, '');
}
